/**
 * Human-readable labels + groups for the admin sprite library UI.
 */
import type { MapFurnitureKind } from "@/lib/office/personal-office-layout";
import { FURNITURE_SPRITES } from "@/lib/office/sprites/catalog";

export type SpriteLibraryGroup =
  | "seating"
  | "work"
  | "decor"
  | "tech"
  | "food"
  | "structure"
  | "other";

export const SPRITE_LIBRARY_GROUP_LABELS: Record<SpriteLibraryGroup, string> = {
  seating: "Seating",
  work: "Workstations",
  decor: "Decor & fun",
  tech: "Tech",
  food: "Food & drink",
  structure: "Structure",
  other: "Other",
};

const GROUP_BY_KIND: Partial<Record<MapFurnitureKind, SpriteLibraryGroup>> = {
  chair: "seating",
  couch: "seating",
  sofa1: "seating",
  cushion: "seating",
  beanbag: "seating",
  stool: "seating",
  pod: "seating",
  desk: "work",
  table: "work",
  counter: "work",
  conftable: "work",
  podium: "work",
  whiteboard: "work",
  bookshelf: "work",
  reception: "work",
  coffeetable: "work",
  laptop: "tech",
  printer: "tech",
  server: "tech",
  screen: "tech",
  mic: "tech",
  cooler: "food",
  coffee: "food",
  fridge: "food",
  plant: "decor",
  cactus: "decor",
  fox: "decor",
  arcade: "decor",
  trophy: "decor",
  sign: "decor",
  lamp: "decor",
  neon: "decor",
  guitar: "decor",
  bike: "decor",
  fishbowl: "decor",
  easel: "decor",
  flag: "decor",
  jukebox: "decor",
  statue: "decor",
  mat: "decor",
  rug: "decor",
  clock: "decor",
  bin: "other",
  locker: "other",
  wall: "structure",
  gwall: "structure",
  gwallW: "structure",
  door: "structure",
  building: "structure",
  tree: "decor",
  bush: "decor",
  bench: "seating",
  streetlamp: "decor",
  fountain: "decor",
  plaza_sign: "decor",
  delivery_truck: "decor",
  cafe_umbrella: "decor",
  pine: "decor",
  rock: "decor",
  flower: "decor",
  hedge: "decor",
  planter: "decor",
  bollard: "decor",
  fence: "decor",
  flowerbed: "decor",
};

const LABEL_BY_KIND: Partial<Record<MapFurnitureKind, string>> = {
  desk: "Desk",
  chair: "Chair",
  plant: "Plant",
  couch: "Purple lounge couch",
  sofa1: "Green lounge sofa",
  table: "Table (2×1)",
  counter: "Counter (3×1)",
  reception: "Reception desk",
  coffeetable: "Coffee table",
  cooler: "Water cooler",
  conftable: "Conference table",
  screen: "Presentation screen",
  podium: "Podium",
  cushion: "Floor cushion",
  fox: "Atlas fox",
  arcade: "Arcade cabinet",
  trophy: "Trophy",
  sign: "Vyllage sign",
  pod: "Focus pod",
  lamp: "Desk lamp",
  cactus: "Cactus",
  bookshelf: "Bookshelf",
  beanbag: "Beanbag",
  whiteboard: "Whiteboard",
  coffee: "Espresso cart",
  printer: "Printer",
  bin: "Bin",
  clock: "Wall clock",
  fridge: "Fridge",
  locker: "Locker",
  guitar: "Guitar",
  bike: "Bike",
  fishbowl: "Fishbowl",
  easel: "Easel",
  flag: "Flag",
  neon: "Neon sign",
  mic: "Mic stand",
  stool: "Stool",
  jukebox: "Jukebox",
  laptop: "Laptop",
  server: "Server rack",
  mat: "Floor mat",
  statue: "Hero bust",
  rug: "Area rug",
  wall: "Solid wall",
  gwall: "Glass wall",
  gwallW: "Glass wall (W)",
  door: "Door",
  building: "Outdoor building",
  tree: "Tree",
  bush: "Bush",
  bench: "Park bench",
  streetlamp: "Street lamp",
  fountain: "Plaza fountain",
  plaza_sign: "Plaza sign",
  delivery_truck: "Delivery truck",
  cafe_umbrella: "Cafe umbrella table",
  pine: "Pine tree",
  rock: "Rock",
  flower: "Flower",
  hedge: "Hedge",
  planter: "Planter box",
  bollard: "Bollard",
  fence: "Fence section",
  flowerbed: "Flower bed",
};

export type SpriteLibraryItem = {
  kind: MapFurnitureKind;
  label: string;
  group: SpriteLibraryGroup;
  thumbSrc: string | null;
  assetSrc: string | null;
  footprint: { w: number; d: number };
  seat: boolean;
  block: boolean;
  useSprite: boolean;
};

export function furnitureLabel(kind: string): string {
  const known = LABEL_BY_KIND[kind as MapFurnitureKind];
  if (known) return known;
  return kind.replace(/_/g, " ").replace(/\b\w/g, (c) => c.toUpperCase());
}

/** Full catalog for the admin sprite library browser. */
export function listSpriteLibraryItems(): SpriteLibraryItem[] {
  const items: SpriteLibraryItem[] = [];
  for (const [kind, def] of Object.entries(FURNITURE_SPRITES) as Array<
    [MapFurnitureKind, (typeof FURNITURE_SPRITES)[MapFurnitureKind]]
  >) {
    const variant = def.variants[0] ?? null;
    items.push({
      kind,
      label: furnitureLabel(kind),
      group: GROUP_BY_KIND[kind] ?? "other",
      thumbSrc: def.thumbSrc ?? variant?.src ?? null,
      assetSrc: variant?.src ?? null,
      footprint: def.footprint,
      seat: def.defaultSeat === true,
      block: def.defaultBlock === true,
      useSprite: def.useSprite !== false,
    });
  }
  return items.sort((a, b) => a.label.localeCompare(b.label));
}
